import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import './CatalogueCard.css';

export interface CatalogueCardProps {
  id: string;
  image?: string;
  title: string;
  subtitle?: string;
  price?: string;
  description?: string;
  category?: string;
  tags?: string;
  packageName?: string;
  packageImage?: string;
  onCardClick?: (id: string) => void;
  onApprove?: (id: string) => Promise<void>;
  onSuspend?: (id: string) => Promise<void>;
  variant?: 'service' | 'profile';
}

const CatalogueCard: React.FC<CatalogueCardProps> = ({
  id,
  image,
  title,
  subtitle,
  price,
  description,
  category,
  tags,
  packageName,
  onCardClick,
  onApprove,
  onSuspend,
  variant = 'service',
}) => {
  const [approving, setApproving] = useState(false);
  const [suspending, setSuspending] = useState(false);
  
  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick(id);
    }
  };

  const handleApprove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onApprove) {
      setApproving(true);
      await onApprove(id);
      setApproving(false);
    }
  };

  const handleSuspend = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSuspend) {
      setSuspending(true);
      await onSuspend(id);
      setSuspending(false);
    }
  };

  const disableActions = approving || suspending;

  return (
    <Box
      className={`catalogue-card catalogue-card--${variant}`}
      onClick={handleCardClick}
      sx={{
        backgroundColor: 'var(--palette-light-background-primary-value-hex)',
        borderRadius: 'var(--palette-light-4-value)', // 16px
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--palette-light-3-value)', // 12px
        paddingBottom: 'var(--palette-light-4-value)', // 16px
        minWidth: variant === 'service' ? '245px' : '332px',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      {/* Image */}
      <Box
        component="img"
        src={image}
        alt={title}
        sx={{
          width: '100%',
          height: variant === 'service' ? '244.76px' : '169.73px',
          borderRadius: 'var(--palette-light-4-value)', // 16px
          objectFit: 'cover',
        }}
        onError={(e: any) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }}
      />
      <Box
        sx={{
          width: '100%',
          height: variant === 'service' ? '244.76px' : '169.73px',
          borderRadius: 'var(--palette-light-4-value)', // 16px
          backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
          display: 'none', // Hidden by default, shown on image error
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: 'var(--palette-light-font-size-h3-value)',
            fontWeight: 700,
            color: 'var(--palette-light-text-primary-value-hex)',
          }}
        >
          {title.charAt(0)}
        </Typography>
      </Box>

      {/* Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--palette-light-1-value)',
          padding: '0 var(--palette-light-4-value)',
        }}
      >
        {/* Subtitle (vendor name for services) */}
        {subtitle && (
          <Typography
            sx={{
              fontSize: variant === 'service' ? 'var(--palette-light-font-size-small-value)' : 'var(--palette-light-font-size-default-value)',
              fontWeight: variant === 'service' ? 400 : 700,
              color: 'var(--palette-light-brand-primary-value-hex)',
            }}
          >
            {subtitle}
          </Typography>
        )}

        {/* Title */}
        <Typography
          sx={{
            fontSize: 'var(--palette-light-font-size-small-value)',
            fontWeight: 700,
            color: 'var(--palette-light-text-primary-value-hex)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography>

        {/* Price */}
        {price && (
          <Typography
            sx={{
              fontSize: 'var(--palette-light-font-size-small-value)',
              fontWeight: 700,
              color: 'var(--palette-light-brand-primary-value-hex)',
            }}
          >
            {price}
          </Typography>
        )}

        {/* Description */}
        {description && (
          <Typography
            sx={{
              fontSize: 'var(--palette-light-font-size-small-value)',
              color: 'var(--palette-light-text-secondary-value-hex)',
              height: '40px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {description}
          </Typography>
        )}

        {variant === 'service' && packageName ? (
          <Typography
            sx={{
              fontSize: 'var(--palette-light-font-size-small-value)',
              color: 'var(--palette-light-text-secondary-value-hex)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={packageName}
          >
            {packageName}
          </Typography>
        ) : null}

        {/* Category and Tags (for profiles) */}
        {variant === 'profile' && category && tags && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--palette-light-2-value)',
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{
                fontSize: 'var(--palette-light-font-size-small-value)',
                color: 'var(--palette-light-text-primary-value-hex)',
                flexShrink: 0,
              }}
            >
              {category}
            </Typography>
            <Typography
              sx={{
                fontSize: 'var(--palette-light-font-size-small-value)',
                color: 'var(--palette-light-text-primary-value-hex)',
                flexShrink: 0,
              }}
            >
              •
            </Typography>
            <Typography
              sx={{
                fontSize: 'var(--palette-light-font-size-small-value)',
                color: 'var(--palette-light-text-primary-value-hex)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {tags}
            </Typography>
          </Box>
        )}

      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: 'var(--palette-light-2-value)',
          padding: '0 var(--palette-light-4-value)',
        }}
      >
        <Button
          variant="contained"
          loading={approving}
          disabled={disableActions}
          onClick={handleApprove}
          sx={{
            flex: 1,
            height: 'var(--palette-light-8-value)', // 32px
            borderRadius: 'var(--palette-light-4-value)', // 16px
            backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
            color: 'var(--palette-light-text-foreground-value-hex)',
            fontSize: 'var(--palette-light-font-size-small-value)',
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
              opacity: 0.9,
            },
          }}
        >
          Approve
        </Button>
        <Button
          variant="outlined"
          loading={suspending}
          disabled={disableActions}
          onClick={handleSuspend}
          sx={{
            flex: 1,
            height: 'var(--palette-light-8-value)', // 32px
            borderRadius: 'var(--palette-light-4-value)', // 16px
            borderColor: 'rgba(103, 49, 79, 0.4)',
            color: 'var(--palette-light-brand-primary-value-hex)',
            fontSize: 'var(--palette-light-font-size-small-value)',
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': {
              borderColor: 'var(--palette-light-brand-primary-value-hex)',
              backgroundColor: 'rgba(103, 49, 79, 0.04)',
            },
          }}
        >
          Suspend
        </Button>
      </Box>
    </Box>
  );
};

export default CatalogueCard;

